import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCalculator, FaPlus, FaMinus, FaCheck, FaTimes, FaArrowRight, FaBuilding, FaShoppingCart, FaHome, FaCog } from 'react-icons/fa';

const Calculator = () => {
  const [websiteType, setWebsiteType] = useState('basic');
  const [additionalPages, setAdditionalPages] = useState([]);
  const [paymentSystems, setPaymentSystems] = useState([]);
  const [proServices, setProServices] = useState([]);
  const [total, setTotal] = useState(0);
  const [showQuote, setShowQuote] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_details: '',
    message: ''
  });

  // Pricing Configuration - All prices reduced by 25%
  // Original prices multiplied by 0.75 (25% off)
  const pricing = {
    websiteTypes: {
      basic: { name: 'Basic Website', price: 5625, description: '5-10 pages, responsive design' }, // 7500 * 0.75
      ecommerce: { name: 'E-commerce Website', price: 18750, description: 'Online store with payment integration' }, // 25000 * 0.75
      real_estate: { name: 'Real Estate Website', price: 22500, description: 'Property listings with search & filters' }, // 30000 * 0.75
      corporate: { name: 'Corporate Website', price: 11250, description: 'Professional business website' }, // 15000 * 0.75
      portfolio: { name: 'Portfolio Website', price: 7500, description: 'Creative portfolio showcase' }, // 10000 * 0.75
      custom: { name: 'Custom Website', price: 0, description: 'Tailor-made solution' }
    },
    additionalPages: [
      { id: 'about', name: 'About Us', price: 750 }, // 1000 * 0.75
      { id: 'blog', name: 'Blog', price: 750 }, // 1000 * 0.75
      { id: 'contact', name: 'Contact Us', price: 750 }, // 1000 * 0.75
      { id: 'gallery', name: 'Gallery', price: 750 }, // 1000 * 0.75
      { id: 'landing', name: 'Landing Page', price: 750 }, // 1000 * 0.75
      { id: 'product_detail', name: 'Product Detail Page', price: 750 }, // 1000 * 0.75
      { id: 'product_list', name: 'Product Page', price: 2250 }, // 3000 * 0.75
      { id: 'service', name: 'Service Page', price: 750 }, // 1000 * 0.75
      { id: 'testimonial', name: 'Testimonial', price: 750 } // 1000 * 0.75
    ],
    paymentSystems: [
      { id: 'chapa', name: 'Chapa', price: 3750 }, // 5000 * 0.75
      { id: 'international', name: 'International Payment', price: 15000 }, // 20000 * 0.75
      { id: 'telebirr', name: 'TeleBirr', price: 7500 } // 10000 * 0.75
    ],
    proServices: [
      { id: 'blog_platform', name: 'Blog Platform', price: 2250 }, // 3000 * 0.75
      { id: 'booking', name: 'Booking System', price: 3000 }, // 4000 * 0.75
      { id: 'analytics', name: 'Google Analytics', price: 2250 }, // 3000 * 0.75
      { id: 'membership', name: 'Membership System', price: 1875 }, // 2500 * 0.75
      { id: 'multi_vendor', name: 'Multi Vendor for E-commerce', price: 3000 }, // 4000 * 0.75
      { id: 'seo', name: 'SEO Optimization', price: 2250 }, // 3000 * 0.75
      { id: 'content_creation', name: 'Website Content Creation', price: 1500 } // 2000 * 0.75
    ]
  };

  // Toggle selection functions
  const toggleAdditionalPage = (pageId) => {
    setAdditionalPages(prev =>
      prev.includes(pageId) 
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const togglePaymentSystem = (systemId) => {
    setPaymentSystems(prev =>
      prev.includes(systemId) 
        ? prev.filter(id => id !== systemId)
        : [...prev, systemId]
    );
  };

  const toggleProService = (serviceId) => {
    setProServices(prev =>
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Calculate total
  useEffect(() => {
    let totalPrice = 0;

    // Website type
    const websitePrice = pricing.websiteTypes[websiteType]?.price || 0;
    totalPrice += websitePrice;

    // Additional pages
    additionalPages.forEach(pageId => {
      const page = pricing.additionalPages.find(p => p.id === pageId);
      if (page) totalPrice += page.price;
    });

    // Payment systems
    paymentSystems.forEach(systemId => {
      const system = pricing.paymentSystems.find(p => p.id === systemId);
      if (system) totalPrice += system.price;
    });

    // Pro services
    proServices.forEach(serviceId => {
      const service = pricing.proServices.find(p => p.id === serviceId);
      if (service) totalPrice += service.price;
    });

    setTotal(totalPrice);
  }, [websiteType, additionalPages, paymentSystems, proServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      alert('Please fill in your name and email');
      return;
    }

    const quoteData = {
      ...formData,
      website_type: websiteType,
      additional_pages: additionalPages,
      payment_systems: paymentSystems,
      pro_services: proServices,
      total_price: total
    };

    try {
      const response = await axios.post(
        'http://localhost/senidek-technology/backend/api.php?endpoint=quote',
        quoteData
      );
      
      if (response.data.success) {
        alert('✅ Quote request sent successfully! We will contact you within 24 hours.');
        setShowQuote(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          project_details: '',
          message: ''
        });
      }
    } catch (error) {
      alert('❌ Error sending request. Please try again.');
      console.error(error);
    }
  };

  return (
    <div className="calculator page-enter">
      <div className="calculator-header">
        <span className="calculator-badge">💰 Calculate Your Project Cost</span>
        <h2>Get an Instant <span>Estimate</span></h2>
        <p>Select the services you need and see the total cost in real-time</p>
        <div className="discount-banner">
          🎉 <strong>25% Discount</strong> on all services! Limited time offer.
        </div>
      </div>

      <div className="calculator-container">
        {/* Website Type */}
        <div className="calculator-section">
          <h3><FaBuilding /> Select Website Type</h3>
          <div className="option-grid">
            {Object.entries(pricing.websiteTypes).map(([key, value]) => (
              <div
                key={key}
                className={`option-card ${websiteType === key ? 'selected' : ''}`}
                onClick={() => setWebsiteType(key)}
              >
                <div className="option-check">
                  {websiteType === key ? <FaCheck /> : <div className="empty-circle" />}
                </div>
                <div className="option-content">
                  <h4>{value.name}</h4>
                  <p>{value.description}</p>
                  <span className="option-price">
                    {value.price > 0 ? `${value.price.toLocaleString()} Birr` : 'Custom Pricing'}
                    {value.price > 0 && <span className="original-price"> (was {Math.round(value.price / 0.75).toLocaleString()} Birr)</span>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Pages */}
        <div className="calculator-section">
          <h3><FaPlus /> Additional Pages <span className="section-hint">(750 Birr each - was 1,000 Birr)</span></h3>
          <div className="option-grid pages-grid">
            {pricing.additionalPages.map(page => (
              <div
                key={page.id}
                className={`option-card small ${additionalPages.includes(page.id) ? 'selected' : ''}`}
                onClick={() => toggleAdditionalPage(page.id)}
              >
                <div className="option-check">
                  {additionalPages.includes(page.id) ? <FaCheck /> : <div className="empty-circle" />}
                </div>
                <div className="option-content">
                  <span className="option-name">{page.name}</span>
                  <span className="option-price">{page.price.toLocaleString()} Birr</span>
                </div>
              </div>
            ))}
          </div>
          <div className="page-count">
            <span>Selected: <strong>{additionalPages.length}</strong> pages</span>
            <span>Total: <strong>{(additionalPages.length * 750).toLocaleString()} Birr</strong></span>
            <span className="discount-note">(Saved: {(additionalPages.length * 250).toLocaleString()} Birr)</span>
          </div>
        </div>

        {/* Payment Systems */}
        <div className="calculator-section">
          <h3><FaShoppingCart /> Payment Systems</h3>
          <div className="option-grid payment-grid">
            {pricing.paymentSystems.map(system => (
              <div
                key={system.id}
                className={`option-card ${paymentSystems.includes(system.id) ? 'selected' : ''}`}
                onClick={() => togglePaymentSystem(system.id)}
              >
                <div className="option-check">
                  {paymentSystems.includes(system.id) ? <FaCheck /> : <div className="empty-circle" />}
                </div>
                <div className="option-content">
                  <span className="option-name">{system.name}</span>
                  <span className="option-price">{system.price.toLocaleString()} Birr</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pro Services */}
        <div className="calculator-section">
          <h3><FaCog /> Additional Pro Services</h3>
          <div className="option-grid pro-grid">
            {pricing.proServices.map(service => (
              <div
                key={service.id}
                className={`option-card ${proServices.includes(service.id) ? 'selected' : ''}`}
                onClick={() => toggleProService(service.id)}
              >
                <div className="option-check">
                  {proServices.includes(service.id) ? <FaCheck /> : <div className="empty-circle" />}
                </div>
                <div className="option-content">
                  <span className="option-name">{service.name}</span>
                  <span className="option-price">{service.price.toLocaleString()} Birr</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total Summary */}
        <div className="calculator-summary">
          <div className="summary-left">
            <h3>Total Summary</h3>
            <div className="summary-items">
              <div className="summary-item">
                <span>Website Type</span>
                <span>{pricing.websiteTypes[websiteType]?.name || 'None'}</span>
              </div>
              <div className="summary-item">
                <span>Additional Pages</span>
                <span>{additionalPages.length} pages</span>
              </div>
              <div className="summary-item">
                <span>Payment Systems</span>
                <span>{paymentSystems.length} selected</span>
              </div>
              <div className="summary-item">
                <span>Pro Services</span>
                <span>{proServices.length} selected</span>
              </div>
            </div>
            <div className="summary-total">
              <span>Total (without VAT)</span>
              <span className="total-price">{total.toLocaleString()} Birr</span>
            </div>
            <div className="summary-savings">
              <span>You saved</span>
              <span className="savings-price">{Math.round(total * 0.333).toLocaleString()} Birr</span>
            </div>
          </div>
          <div className="summary-right">
            <button className="btn-primary get-quote-btn" onClick={() => setShowQuote(true)}>
              <FaArrowRight /> Get Quote
            </button>
            <p>Receive a detailed quote with VAT breakdown</p>
            <p className="discount-note">🎉 25% discount applied to all services</p>
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      {showQuote && (
        <div className="modal-overlay show" onClick={() => setShowQuote(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Get Your Quote</h2>
              <button className="btn-close" onClick={() => setShowQuote(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+251 9XX XXX XXX"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Project Details</label>
                <textarea
                  value={formData.project_details}
                  onChange={(e) => setFormData({...formData, project_details: e.target.value})}
                  rows="3"
                  placeholder="Tell us more about your project..."
                />
              </div>
              <div className="form-group">
                <label>Additional Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="3"
                  placeholder="Any special requirements?"
                />
              </div>
              <div className="quote-summary">
                <h4>Estimated Total</h4>
                <p><strong>{total.toLocaleString()} Birr</strong> (without VAT)</p>
                <p className="quote-note">🎉 25% discount applied • Final price may vary based on specific requirements</p>
              </div>
              <button type="submit" className="btn-primary submit-btn">
                Send Quote Request
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .calculator {
          padding: 20px 0;
          max-width: 1200px;
          margin: 0 auto;
        }

        .calculator-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .calculator-badge {
          display: inline-block;
          background: var(--primary-light);
          color: var(--primary);
          padding: 6px 20px;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .calculator-header h2 {
          font-size: 2.6rem;
          font-weight: 800;
          color: var(--gray-900);
        }

        .calculator-header h2 span {
          color: var(--primary);
          position: relative;
        }

        .calculator-header p {
          font-size: 1.1rem;
          color: var(--gray-500);
          margin-top: 8px;
        }

        .discount-banner {
          display: inline-block;
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          color: #92400e;
          padding: 10px 24px;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 500;
          margin-top: 12px;
          border: 2px solid #f59e0b;
        }

        .calculator-section {
          background: white;
          border-radius: 20px;
          padding: 28px;
          margin-bottom: 24px;
          border: 1px solid var(--gray-200);
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }

        .calculator-section h3 {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--gray-800);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .calculator-section h3 .section-hint {
          font-size: 0.8rem;
          font-weight: 400;
          color: var(--gray-500);
        }

        .option-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 12px;
        }

        .option-grid.pages-grid {
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        }

        .option-grid.payment-grid {
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        }

        .option-grid.pro-grid {
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        }

        .option-card {
          border: 2px solid var(--gray-200);
          border-radius: 12px;
          padding: 14px 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .option-card:hover {
          border-color: var(--primary-light);
          background: var(--gray-50);
        }

        .option-card.selected {
          border-color: var(--primary);
          background: var(--primary-light);
        }

        .option-card.small {
          padding: 10px 14px;
        }

        .option-check {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .option-check .empty-circle {
          width: 20px;
          height: 20px;
          border: 2px solid var(--gray-300);
          border-radius: 50%;
        }

        .option-check svg {
          color: var(--primary);
          font-size: 1.2rem;
        }

        .option-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .option-content h4 {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--gray-800);
        }

        .option-content p {
          font-size: 0.8rem;
          color: var(--gray-500);
        }

        .option-name {
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        .option-price {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--primary);
          margin-top: 2px;
        }

        .original-price {
          font-size: 0.7rem;
          font-weight: 400;
          color: var(--gray-400);
          text-decoration: line-through;
          margin-left: 4px;
        }

        .option-card.selected .option-price {
          color: var(--primary-dark);
        }

        .page-count {
          margin-top: 12px;
          padding: 12px 16px;
          background: var(--gray-50);
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }

        .discount-note {
          font-size: 0.8rem;
          color: var(--success);
          font-weight: 500;
        }

        .calculator-summary {
          background: linear-gradient(135deg, var(--gray-50), white);
          border-radius: 20px;
          padding: 28px 32px;
          border: 2px solid var(--gray-200);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          align-items: center;
        }

        .summary-left h3 {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .summary-items {
          display: grid;
          gap: 6px;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          font-size: 0.9rem;
          color: var(--gray-600);
          border-bottom: 1px solid var(--gray-100);
        }

        .summary-total {
          display: flex;
          justify-content: space-between;
          padding-top: 12px;
          margin-top: 8px;
          border-top: 2px solid var(--gray-200);
          font-size: 1.2rem;
          font-weight: 700;
        }

        .summary-total .total-price {
          color: var(--primary);
          font-size: 1.4rem;
        }

        .summary-savings {
          display: flex;
          justify-content: space-between;
          padding-top: 8px;
          margin-top: 4px;
          font-size: 0.95rem;
          color: var(--success);
          font-weight: 600;
        }

        .savings-price {
          color: var(--success);
          font-size: 1.1rem;
        }

        .summary-right {
          text-align: center;
        }

        .get-quote-btn {
          padding: 16px 40px;
          font-size: 1.1rem;
          width: 100%;
          justify-content: center;
        }

        .summary-right p {
          margin-top: 8px;
          font-size: 0.85rem;
          color: var(--gray-500);
        }

        .summary-right .discount-note {
          color: var(--success);
          font-weight: 600;
          font-size: 0.9rem;
        }

        .quote-summary {
          background: var(--gray-50);
          padding: 16px 20px;
          border-radius: 12px;
          margin-bottom: 16px;
        }

        .quote-summary h4 {
          font-size: 1rem;
          margin-bottom: 4px;
        }

        .quote-summary p {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--primary);
        }

        .quote-summary .quote-note {
          font-size: 0.8rem;
          font-weight: 400;
          color: var(--gray-500);
          margin-top: 4px;
        }

        .submit-btn {
          width: 100%;
          justify-content: center;
          padding: 14px;
          font-size: 1.05rem;
        }

        /* Modal */
        .modal-overlay {
          display: ${showQuote ? 'flex' : 'none'};
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(4px);
          z-index: 1000;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          padding: 32px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-header h2 {
          font-size: 1.4rem;
          font-weight: 700;
        }

        .btn-close {
          background: transparent;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #94a3b8;
        }

        .btn-close:hover { color: #0f172a; }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 4px;
          font-size: 0.9rem;
          color: #0f172a;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid var(--gray-200);
          border-radius: 12px;
          font-size: 0.95rem;
          font-family: inherit;
          transition: all 0.3s ease;
          background: var(--gray-50);
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 4px rgba(37,99,235,0.1);
          background: white;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 768px) {
          .calculator-summary {
            grid-template-columns: 1fr;
          }

          .option-grid {
            grid-template-columns: 1fr;
          }

          .option-grid.pages-grid,
          .option-grid.payment-grid,
          .option-grid.pro-grid {
            grid-template-columns: 1fr 1fr;
          }

          .calculator-header h2 {
            font-size: 2rem;
          }

          .calculator-section {
            padding: 20px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .option-grid.pages-grid,
          .option-grid.payment-grid,
          .option-grid.pro-grid {
            grid-template-columns: 1fr;
          }

          .calculator-header h2 {
            font-size: 1.6rem;
          }

          .discount-banner {
            font-size: 0.85rem;
            padding: 8px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default Calculator;